/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import { MfaChallengeScreen } from "../screens/MfaChallengeScreen.js";
import {
  handleRouteSendChallenge,
  handleRouteSolveChallenge,
} from "../utils/tan.js";
import type { PendingProtectedAction } from "../api/protectedAction.js";
import type { ApiFailure } from "../api/failure.js";
import { normalizeApiFailure } from "../api/failure.js";
import { purgeChallengeIds } from "../api/client.js";
import {
  currentAccount,
  currentBackendUrl,
  isCurrentSessionIdentity,
} from "../stores/session.js";
import { useTranslation } from "../context/translation.js";

export interface MfaChallengeRouteProps {
  action: PendingProtectedAction;
  onComplete: (redirectTo: string) => void;
  onCancel: () => void;
}

/**
 * The gate a sensitive action goes through.
 *
 * The screen was mounted without any way to reach the server, so the code it
 * asked for was never sent and a code that was typed could never be checked —
 * every action behind the gate was a dead end.
 */
export function MfaChallengeRoute({
  action,
  onComplete,
  onCancel,
}: MfaChallengeRouteProps): VNode {
  const { t } = useTranslation();
  const [completionError, setCompletionError] = useState<
    ApiFailure | undefined
  >();
  const [challengeResponse, setChallengeResponse] = useState(
    action.challengeResponse,
  );
  const [solvedChallengeIds, setSolvedChallengeIds] = useState<string[]>([]);
  useEffect(() => {
    setChallengeResponse(action.challengeResponse);
    setSolvedChallengeIds([]);
    setCompletionError(undefined);
  }, [action]);
  const challengeKey = challengeResponse.challenges
    .map((challenge) => challenge.challenge_id)
    .join(":");
  return (
    <MfaChallengeScreen
      key={challengeKey}
      accountLabel={action.accountLabel}
      challengeAccount={action.challengeAccount}
      challengeResponse={challengeResponse}
      solvedChallengeIds={solvedChallengeIds}
      actionNotice={action.actionNotice}
      completionError={completionError}
      onSuccess={async (solvedChallengeIds) => {
        if (action.identity && !isCurrentSessionIdentity(action.identity)) {
          setCompletionError(
            normalizeApiFailure(
              new Error(t`Your session changed. Start this action again.`),
            ),
          );
          return;
        }
        let result;
        try {
          result = await action.continueWith(solvedChallengeIds);
        } catch (cause) {
          result = {
            type: "failure" as const,
            error: normalizeApiFailure(cause, { outcome: "indeterminate" }),
          };
        }
        if (result.type === "failure") {
          purgeChallengeIds(solvedChallengeIds);
          setCompletionError(result.error);
          try {
            await action.revalidate?.();
          } catch {
            // The protected mutation is the primary failure. The target read
            // owns and displays any revalidation failure separately.
          }
          return;
        }
        if (result.type === "challenge") {
          // Some policies require more than one challenge round. The original
          // request body stays captured by continueWith; only the challenge
          // response changes here.
          setCompletionError(undefined);
          setSolvedChallengeIds(solvedChallengeIds);
          setChallengeResponse(result.challenge);
          return;
        }
        purgeChallengeIds(solvedChallengeIds);
        onComplete(result.value.redirectTo);
      }}
      onCancel={() => {
        purgeChallengeIds(solvedChallengeIds);
        onCancel();
      }}
      onSendChallenge={(challengeId) => {
        if (action.identity && !isCurrentSessionIdentity(action.identity)) {
          setCompletionError(
            normalizeApiFailure(
              new Error(t`Your session changed. Start this action again.`),
            ),
          );
          return Promise.resolve("never" as const);
        }
        const account = action.challengeAccount || currentAccount.value;
        if (!account) {
          setCompletionError(
            normalizeApiFailure(
              new Error(
                t`Merchant account context is missing. Start this action again.`,
              ),
            ),
          );
          return Promise.resolve("never" as const);
        }
        return handleRouteSendChallenge(
          challengeId,
          {
            account,
            backendUrl: currentBackendUrl.value,
          },
          t,
        );
      }}
      onSolveChallenge={(challengeId, code) => {
        if (action.identity && !isCurrentSessionIdentity(action.identity)) {
          return Promise.resolve({
            ok: false,
            errorMsg: t`Your session changed. Start this action again.`,
          });
        }
        const account = action.challengeAccount || currentAccount.value;
        if (!account) {
          return Promise.resolve({
            ok: false,
            errorMsg: t`Merchant account context is missing. Start this action again.`,
          });
        }
        return handleRouteSolveChallenge(
          challengeId,
          code,
          {
            account,
            backendUrl: currentBackendUrl.value,
          },
          t,
        );
      }}
    />
  );
}
